import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confidentialAPI } from '../services/api';
import toast from '../utils/toast';

export const useOffboardingCases = (params = {}) => {
  return useQuery({
    queryKey: ['offboarding', 'list', params],
    queryFn: async () => {
      const response = await confidentialAPI.getOffboardingCases(params);
      return response.data?.cases || [];
    },
    staleTime: 60 * 1000,
  });
};

export const useOffboardingCase = (workflowId) => {
  return useQuery({
    queryKey: ['offboarding', 'detail', workflowId],
    queryFn: async () => {
      const response = await confidentialAPI.getOffboardingCase(workflowId);
      return response.data?.case || null;
    },
    enabled: !!workflowId,
    staleTime: 30 * 1000,
  });
};

export const useMyOffboardingCase = (options = {}) => {
  return useQuery({
    queryKey: ['offboarding', 'mine'],
    queryFn: async () => {
      const response = await confidentialAPI.getMyOffboardingCase();
      return response.data?.case || null;
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useAssignedOffboardingTasks = (options = {}) => {
  return useQuery({
    queryKey: ['offboarding', 'assigned'],
    queryFn: async () => {
      const response = await confidentialAPI.getAssignedOffboardingTasks();
      return response.data?.tasks || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

const CLOSED_TASK_STATUSES = new Set(['completed', 'skipped', 'waived']);

export const countOpenEmployeeOffboardingTasks = (offboardingCase) => {
  if (!offboardingCase?.tasks) return 0;
  return offboardingCase.tasks.filter(
    (task) => (
      task.actor === 'Employee'
      && task.task_key !== 'employee_acknowledgement'
      && !CLOSED_TASK_STATUSES.has(task.status)
    ),
  ).length;
};

const invalidateOffboarding = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['offboarding'] });
};

export const useReleaseOffboardingPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => confidentialAPI.releaseOffboardingPlan(id),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Sent to the employee');
    },
    onError: (error) => {
      const details = error.response?.data?.error || {};
      toast.error(details.release || 'Failed to send to the employee');
    },
  });
};

export const useCompleteOffboardingTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, taskKey, data }) => confidentialAPI.completeOffboardingTask(id, taskKey, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Task completed');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to complete task'));
    },
  });
};

export const useCloseOffboardingCase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => confidentialAPI.closeOffboardingCase(id),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Offboarding case closed');
    },
    onError: (error) => {
      const blocking = error.response?.data?.error?.blocking;
      toast.error(blocking || 'Cannot close the case yet');
    },
  });
};

export const useRecordOffboardingDeparture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordOffboardingDeparture(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Repatriation arrangements recorded');
    },
    onError: (error) => {
      const details = error.response?.data?.error || {};
      toast.error(
        details.departure_date
        || details.special_pass_expiry_date
        || details.flight_ticket
        || details.task_key
        || 'Failed to record repatriation arrangements',
      );
    },
  });
};

const complianceError = (error, fallback) => {
  const details = error.response?.data?.error;
  if (typeof details === 'string') return details;
  if (details && typeof details === 'object') {
    return Object.values(details).find(Boolean) || fallback;
  }
  return fallback;
};

export const useConfirmOffboardingDeparture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.confirmOffboardingDeparture(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Departure confirmed');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to confirm departure'));
    },
  });
};

export const useRecordWorkPassCancellation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordOffboardingWorkPassCancellation(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Work-pass cancellation recorded');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to record work-pass cancellation'));
    },
  });
};

export const useRecordIR21Filing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordOffboardingIR21Filing(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('IR21 filing recorded');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to record IR21 filing'));
    },
  });
};

export const useRecordIR21Exemption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordOffboardingIR21Exemption(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('IR21 exemption verification recorded');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to record IR21 exemption'));
    },
  });
};

export const useRecordIR21Directive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordOffboardingIR21Directive(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('IRAS directive recorded');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to record IRAS directive'));
    },
  });
};

export const useUploadIR21DirectiveDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      return confidentialAPI.uploadOffboardingIR21DirectiveDocument(id, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offboarding'] });
      queryClient.invalidateQueries({ queryKey: ['offboarding', 'ir21-directive'] });
      toast.info('Letter uploaded. Review the extracted values against the document.');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to upload the IRAS letter'));
    },
  });
};

export const useConfirmIR21DirectiveFromDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.confirmOffboardingIR21DirectiveFromDocument(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offboarding'] });
      queryClient.invalidateQueries({ queryKey: ['offboarding', 'ir21-directive'] });
      toast.success('IRAS outcome recorded');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to record the verified IRAS outcome'));
    },
  });
};

export const useIR21DirectiveDocumentBlob = (workflowId, documentId) => {
  return useQuery({
    queryKey: ['offboarding', 'ir21-directive', 'document', workflowId, documentId],
    queryFn: async () => {
      const response = await confidentialAPI.downloadOffboardingIR21DirectiveDocument(
        workflowId,
        documentId,
      );
      return response.data;
    },
    enabled: Boolean(workflowId && documentId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useRecordIR21Payment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordOffboardingIR21Payment(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('IRAS payment recorded');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to record IRAS payment'));
    },
  });
};

export const useSetOffboardingDates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.setOffboardingDates(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Dates updated');
    },
    onError: () => toast.error('Failed to update dates'),
  });
};

export const useRecordFullLeaveEncashment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => confidentialAPI.recordFullLeaveEncashment(id),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Annual-leave settlement confirmed');
    },
    onError: (error) => {
      toast.error(complianceError(error, 'Failed to confirm annual-leave settlement'));
    },
  });
};

export const useProposeLeaveOffset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.proposeLeaveOffset(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Notice-offset proposal sent to your manager');
    },
    onError: () => toast.error('Failed to submit the offset proposal'),
  });
};

export const usePreviewLeaveOffset = () => {
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.previewLeaveOffset(id, data),
  });
};

export const useRecordAgreedLeaveOffset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordAgreedLeaveOffset(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Agreed employment end date recorded');
    },
    onError: (error) => {
      invalidateOffboarding(queryClient);
      const response = error.response?.data || {};
      const details = response.error || {};
      const message = (
        details.revised_end_date
        || details.offset_days
        || details.notes
        || details.manager
        || details.payroll
        || details.reapproval
      );
      toast.error(message || 'Failed to record the agreed end date');
    },
  });
};

export const useDecideLeaveOffset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, data }) => confidentialAPI.decideLeaveOffset(requestId, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Offset decision recorded');
    },
    onError: () => toast.error('Failed to record the offset decision'),
  });
};

export const useProcessLeaveOffset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, data }) => confidentialAPI.processLeaveOffset(requestId, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.success('Employment end date updated from approved offset');
    },
    onError: (error) => {
      const code = error.response?.data?.code;
      if (code === 'reapproval_required') {
        toast.warning('Dates changed — manager must approve again');
        return;
      }
      if (code === 'payroll_locked') {
        toast.warning('Payroll must reopen and recalculate before processing');
        return;
      }
      toast.error('Failed to process the notice offset');
    },
  });
};

export const useEmergencyCutoff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.emergencyOffboardingCutoff(id, data),
    onSuccess: () => {
      invalidateOffboarding(queryClient);
      toast.warning('Emergency access cutoff applied');
    },
    onError: () => toast.error('Failed to apply emergency cutoff'),
  });
};
